function startOfLine(source: string, offset: number) {
  return source.slice(0, offset).lastIndexOf('\n') + 1
}

function endOfLine(source: string, offset: number) {
  const end = source.indexOf('\n', offset)
  return end === -1 ? source.length : end
}

/** Get the start and end of the line that contains this offset. Does not include the `\n` at the end of the line. */
export function getLineRange(source: string, offset: number) {
  if (offset < 0 || offset > source.length) return null
  return {start: startOfLine(source, offset), end: endOfLine(source, offset)}
}

/** Get the start and ends of each line that overlaps the range. */
export function getLineRanges(source: string, from: number, to: number) {
  const ranges = []
  let start = startOfLine(source, from)
  while (start <= to) {
    const end = endOfLine(source, start)
    ranges.push({start, end})
    start = end + 1
  }
  return ranges
}

/**
 * Get the number of newlines that should be prepended before inserting content at the given position.
 * Ensures proper spacing between content and surrounding text.
 *
 * @param input - The full document text
 * @param selectionStart - The position where content will be inserted
 * @returns A string of newlines ('' for no newlines, '\n' for one, '\n\n' for two)
 */
export function getNewlinePrefix(input: string, selectionStart: number): string {
  // if the cursor is at the beginning of the input, don't add a newline
  if (selectionStart === 0) return ''

  if (input[selectionStart - 1] === '\n') {
    // if the cursor already has two newlines before it, don't add another
    if (selectionStart === 1 || input[selectionStart - 2] === '\n') return ''
  }

  const previousNewlineIndex = input.lastIndexOf('\n', selectionStart - 1)
  // if there are non-whitespace characters on the current line, add two newlines
  if (/\S/.test(input.substring(previousNewlineIndex, selectionStart))) {
    return '\n\n'
  }

  // if the current line is empty, add one newline
  return '\n'
}

/**
 * Get the number of newlines that should be appended after inserting content at the given position.
 * Ensures proper spacing between content and surrounding text.
 *
 * @param input - The full document text
 * @param selectionEnd - The position where content will be inserted
 * @returns A string of newlines ('' for no newlines, '\n' for one, '\n\n' for two)
 */
export function getNewlineSuffix(input: string, selectionEnd: number): string {
  // if the cursor is at the end of the input, don't add a newline
  if (input.length === selectionEnd) return ''

  if (input[selectionEnd] === '\n') {
    // if the cursor already has two newlines after it, don't add another
    if (selectionEnd === input.length - 1 || input[selectionEnd + 1] === '\n') return ''
  }

  const nextNewlineIndex = input.indexOf('\n', selectionEnd)
  // if there are non-whitespace characters on the current line, add two newlines
  const lineEnd = nextNewlineIndex === -1 ? input.length : nextNewlineIndex
  if (/\S/.test(input.substring(selectionEnd, lineEnd))) {
    return '\n\n'
  }

  // if the current line is empty, add one newline
  return '\n'
}
